import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import { MessageSquare, Zap, FileText, HelpCircle, ArrowRight, Clock, Users, Calendar as CalendarIcon, X, RefreshCw, ChevronLeft, ChevronRight, AlertCircle, Plus, Sparkles, Eye, Star, Lightbulb, AlertTriangle } from 'lucide-react';
import FeedbackRapido from './FeedbackRapido';
import FeedbackCompleto from './FeedbackCompleto';
import GuiaPreenchimento from './GuiaPreenchimento';
import type { FeedbackStats, Feedback, OneOnOneMeeting } from '@/types/performance';
import { getMyAllReports } from '@/api/workforce';
import type { Employee } from '@/types/workforce';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

// 🚀 NEW: TanStack Query hooks for server state
import {
  useFeedbackStats,
  useFeedbacksList,
  useCreateQuickFeedback,
  useCreateCompleteFeedback,
} from '@/hooks/queries/useFeedbackQueries'
import {
  useUpcomingMeetings,
  useCreateMeeting,
} from '@/hooks/queries/useMeetingQueries'

// 🎨 NEW: Zustand UI store for client state
import { useFeedbackUIStore } from '@/stores/feedback-ui-store'

const FeedbackHub = () => {
  const navigate = useNavigate();
  const { tenantDomain } = useTenant();
  const { user } = useAuth();

  // 🚀 Server State (TanStack Query)
  const {
    data: feedbackStats,
    isLoading: statsLoading,
    isFetching: statsIsFetching,
    error: statsError,
  } = useFeedbackStats()

  const {
    data: sentFeedbacksResponse,
    isLoading: feedbacksLoading,
    isFetching: feedbacksIsFetching,
  } = useFeedbacksList({ type: 'sent', limit: 10 })

  const recentFeedbacks = sentFeedbacksResponse?.items || []

  const {
    data: upcomingMeetings = [],
    isLoading: meetingsLoading,
  } = useUpcomingMeetings({ status: 'confirmed', limit: 10 })

  // 🔄 Mutations
  const createMeetingMutation = useCreateMeeting()

  // 🎨 UI State (Zustand)
  const {
    activeTab,
    setActiveTab,
    isAgendaDialogOpen,
    openAgendaDialog,
    closeAgendaDialog,
    selectedEmployeeId,
    setSelectedEmployeeId,
    resetFormState,
  } = useFeedbackUIStore()

  // 🎯 Local State (only for form inputs)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [meetingDuration, setMeetingDuration] = useState('30');
  const [recurrence, setRecurrence] = useState('none');

  // Real employees from API (TODO: could also be a query hook!)
  const [realEmployees, setRealEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);

  // Feedback detail modal state
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isFeedbackDetailOpen, setIsFeedbackDetailOpen] = useState(false);

  const handleFeedbackClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setIsFeedbackDetailOpen(true);
  };

  // ✅ No more manual useEffect for fetching feedbacks/meetings - handled by TanStack Query!

  // Fetch employees for enhanced display (scoped to user's reports)
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!tenantDomain) return;

      try {
        setEmployeesLoading(true);
        const response = await getMyAllReports({ limit: 100 });
        setRealEmployees(response.items);
      } catch (err: any) {
        console.error('Error fetching employees:', err);
        setRealEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
  }, [tenantDomain]);

  // ✅ Simplified meeting creation handler
  const handleCreateMeeting = async () => {
    if (!selectedEmployeeId || !selectedDate) {
      toast.error('Please select an employee and date');
      return;
    }

    const employee = employees.find(e => e.id === selectedEmployeeId);
    if (!employee) return;

    try {
      await createMeetingMutation.mutateAsync({
        employeeId: selectedEmployeeId,
        employeeName: employee.name,
        employeeRole: employee.position,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        duration: parseInt(meetingDuration),
        recurrence: recurrence as 'none' | 'weekly' | 'biweekly' | 'monthly',
      });

      // ✅ Success! Mutation hook handles:
      // - Toast notification
      // - Cache invalidation
      // - UI updates

      // Just reset form state
      closeAgendaDialog();
      resetFormState();
      setSelectedTime('09:00');
      setMeetingDuration('30');
      setRecurrence('none');
    } catch (err) {
      // ✅ Error handled by mutation hook
      console.error('[FeedbackHub] Error creating meeting:', err);
    }
  };

  // ✅ Derived state - simple boolean logic
  const isLoading = statsLoading || feedbacksLoading || meetingsLoading;
  const isRefreshing = (statsIsFetching && !statsLoading) || (feedbacksIsFetching && !feedbacksLoading);
  const error = statsError ? 'Erro ao carregar dados de feedback.' : null;

  // Map employees for UI display
  const employees = realEmployees.length > 0
    ? realEmployees.map((emp, idx) => ({
        id: emp.employeeId,
        name: emp.personalInfo?.name || 'Unknown',
        avatar: emp.personalInfo?.pictureUrl || `https://i.pravatar.cc/150?img=${idx + 1}`,
        position: emp.currentEngagement?.roleName || 'N/A'
      }))
    : [];

  // Helper function to format relative date
  const formatRelativeDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Hoje';
    if (diffInDays === 1) return '1 dia atrás';
    if (diffInDays < 7) return `${diffInDays} dias atrás`;
    if (diffInDays < 14) return '1 semana atrás';
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} semanas atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  // Helper function to translate recurrence
  const translateRecurrence = (rec: string): string => {
    const translations: Record<string, string> = {
      'none': 'Sem recorrência',
      'weekly': 'Semanal',
      'biweekly': 'Quinzenal',
      'monthly': 'Mensal'
    };
    return translations[rec] || rec;
  };

  // Helper function to translate status
  const translateStatus = (status: string): string => {
    const translations: Record<string, string> = {
      'delivered': 'Entregue',
      'draft': 'Rascunho',
      'follow-up-scheduled': 'Follow-up agendado',
      'scheduled': 'Agendado',
      'confirmed': 'Confirmado',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    };
    return translations[status] || status;
  };

  if (activeTab === 'agenda') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setActiveTab('hub')}
            className="btn-ghost text-brand-gray hover:text-brand-primary"
          >
            ← Voltar ao Hub
          </button>

          <Dialog open={isAgendaDialogOpen} onOpenChange={(open) => open ? openAgendaDialog() : closeAgendaDialog()}>
            <DialogTrigger asChild>
              <button className="btn-primary" onClick={openAgendaDialog}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                Agendar 1-on-1
              </button>
            </DialogTrigger>
            <DialogContent className="bg-brand-dark-light border border-brand-gray/20 text-white max-w-4xl">
              <DialogHeader>
                <DialogTitle className="text-xl text-white">Agendar 1-on-1</DialogTitle>
              </DialogHeader>

              {/* Sincronização de Calendário */}
              <div className="space-y-4">
                <p className="text-brand-gray text-sm">
                  Selecione um calendário para sincronizar o 1-on-1 na sua agenda preferida
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <button className="btn-secondary h-auto py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-monumenta flex items-center justify-center">
                        <CalendarIcon className="h-5 w-5 text-brand-blue" />
                      </div>
                      <span className="text-white">Sincronizar com Google</span>
                    </div>
                  </button>

                  <button className="btn-secondary h-auto py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-brand-blue rounded-monumenta flex items-center justify-center">
                        <CalendarIcon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-white">Sincronizar com Outlook</span>
                    </div>
                  </button>
                </div>

                {/* Layout Grid: Calendário + Formulário */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  {/* Calendário */}
                  <div className="bg-brand-dark-lighter rounded-monumenta p-4 border border-brand-gray/10">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className="rounded-md border-0"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption: "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-sm font-medium",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-brand-gray rounded-md w-9 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-success first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                        day: "h-9 w-9 p-0 font-normal text-brand-gray hover:bg-brand-dark-lighter rounded-monumenta aria-selected:opacity-100",
                        day_selected: "bg-brand-primary text-brand-dark hover:bg-brand-primary/80 hover:text-brand-dark focus:bg-brand-primary focus:text-brand-dark",
                        day_today: "bg-brand-dark-lighter text-white",
                        day_outside: "text-brand-gray opacity-50",
                        day_disabled: "text-brand-gray opacity-50",
                        day_range_middle: "aria-selected:bg-brand-dark aria-selected:text-white",
                        day_hidden: "invisible",
                      }}
                    />
                  </div>

                  {/* Formulário de Configuração */}
                  <div className="space-y-4">
                    {/* Pessoa Vinculada */}
                    <div>
                      <label className="text-brand-gray text-sm mb-2 block">Pessoa Vinculada</label>
                      {employeesLoading ? (
                        <div className="bg-brand-dark-lighter border border-brand-gray/20 rounded-monumenta p-2 text-brand-gray text-sm">
                          Carregando funcionários...
                        </div>
                      ) : employees.length === 0 ? (
                        <div className="bg-brand-dark-lighter border border-brand-gray/20 rounded-monumenta p-2 text-brand-gray text-sm">
                          Nenhum funcionário disponível
                        </div>
                      ) : (
                        <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                          <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white rounded-monumenta">
                            <SelectValue placeholder="Selecione um funcionário">
                              {selectedEmployeeId && (
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={employees.find(e => e.id === selectedEmployeeId)?.avatar} />
                                    <AvatarFallback>
                                      {employees.find(e => e.id === selectedEmployeeId)?.name.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span>{employees.find(e => e.id === selectedEmployeeId)?.name}</span>
                                </div>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                            {employees.map((employee) => (
                              <SelectItem key={employee.id} value={employee.id} className="text-white">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={employee.avatar} />
                                    <AvatarFallback>{employee.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div>{employee.name}</div>
                                    <div className="text-xs text-brand-gray">{employee.position}</div>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Horário de início */}
                    <div>
                      <label className="text-brand-gray text-sm mb-2 block">Horário de início</label>
                      <Select value={selectedTime} onValueChange={setSelectedTime}>
                        <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-brand-gray" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                          {Array.from({ length: 20 }, (_, i) => {
                            const hour = Math.floor(i / 2) + 7;
                            const minute = i % 2 === 0 ? '00' : '30';
                            return `${hour.toString().padStart(2, '0')}:${minute}`;
                          }).map((time) => (
                            <SelectItem key={time} value={time} className="text-white">
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tempo de reunião */}
                    <div>
                      <label className="text-brand-gray text-sm mb-2 block">Tempo de reunião</label>
                      <Select value={meetingDuration} onValueChange={setMeetingDuration}>
                        <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-brand-gray" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                          <SelectItem value="15" className="text-white">15 minutos</SelectItem>
                          <SelectItem value="30" className="text-white">30 minutos</SelectItem>
                          <SelectItem value="45" className="text-white">45 minutos</SelectItem>
                          <SelectItem value="60" className="text-white">60 minutos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Recorrência */}
                    <div>
                      <label className="text-brand-gray text-sm mb-2 block">Recorrência</label>
                      <Select value={recurrence} onValueChange={setRecurrence}>
                        <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 text-brand-gray" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-brand-dark-lighter border-brand-gray/20">
                          <SelectItem value="none" className="text-white">Não repetir</SelectItem>
                          <SelectItem value="weekly" className="text-white">Semanal</SelectItem>
                          <SelectItem value="biweekly" className="text-white">Quinzenal</SelectItem>
                          <SelectItem value="monthly" className="text-white">Mensal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/10">
                  <Button
                    variant="outline"
                    onClick={closeAgendaDialog}
                    className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="bg-error text-white hover:bg-error"
                    onClick={handleCreateMeeting}
                    disabled={createMeetingMutation.isPending}
                  >
                    {createMeetingMutation.isPending ? 'Agendando...' : 'Agendar 1-on-1'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Título da Página */}
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white text-2xl">Agenda de 1-on-1</CardTitle>
                <p className="text-brand-gray text-sm mt-1">Gerencie seus agendamentos de feedback individuais</p>
              </div>
              <CalendarIcon className="h-12 w-12 text-success" />
            </div>
          </CardHeader>
        </Card>

        {/* Lista de Agendamentos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Próximos Agendamentos */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white">Próximos Agendamentos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {meetingsLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-brand-dark-lighter rounded-lg p-4">
                      <Skeleton className="h-10 w-full mb-2 bg-brand-dark" />
                      <Skeleton className="h-6 w-3/4 bg-brand-dark" />
                    </div>
                  ))}
                </>
              ) : upcomingMeetings.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 bg-brand-blue/10 rounded-monumenta flex items-center justify-center">
                      <CalendarIcon className="h-16 w-16 text-brand-blue" />
                    </div>
                  </div>
                  <h3 className="text-white text-lg font-display mb-2">Nenhum 1-on-1 agendado</h3>
                  <p className="text-brand-gray text-sm mb-6 max-w-md mx-auto">
                    Agendamentos regulares de 1-on-1 garantem conversas estruturadas e espaço dedicado para desenvolvimento. Configure recorrências semanais, quinzenais ou mensais.
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={openAgendaDialog}
                      className="btn-primary mx-auto"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agendar Primeiro 1-on-1
                    </button>
                    <button className="btn-secondary mx-auto">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Sincronizar Calendário
                    </button>
                  </div>
                </div>
              ) : (
                upcomingMeetings.map((meeting) => (
                  <div key={meeting.meetingId} className="bg-brand-dark-lighter rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={`https://i.pravatar.cc/150?img=1`} />
                          <AvatarFallback>{meeting.employeeName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-medium">{meeting.employeeName}</p>
                          <p className="text-brand-gray text-xs">{translateRecurrence(meeting.recurrence)}</p>
                        </div>
                      </div>
                      <Badge className={meeting.status === 'confirmed' ? 'bg-success/20 text-success border-success' : 'bg-warning/20 text-warning border-warning'}>
                        {translateStatus(meeting.status)}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-300">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4 text-brand-gray" />
                        <span>{new Date(meeting.date).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-brand-gray" />
                        <span>{meeting.time} ({meeting.duration} min)</span>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1 border-brand-gray/20 text-gray-300 hover:bg-brand-dark">
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 border-error text-error hover:bg-error/20">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Calendário de Visão Geral */}
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white">Visão Mensal</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border-0"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center text-white",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-brand-gray rounded-md w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-success first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                  day: "h-9 w-9 p-0 font-normal text-gray-300 hover:bg-brand-dark rounded-md aria-selected:opacity-100",
                  day_selected: "bg-success text-black hover:bg-success hover:text-black focus:bg-success focus:text-black",
                  day_today: "bg-brand-dark text-white",
                  day_outside: "text-brand-gray opacity-50",
                  day_disabled: "text-brand-gray opacity-50",
                  day_range_middle: "aria-selected:bg-brand-dark aria-selected:text-white",
                  day_hidden: "invisible",
                }}
              />

              {/* Legenda */}
              <div className="mt-4 space-y-2">
                <p className="text-brand-gray text-xs font-medium">Legenda</p>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-success rounded"></div>
                  <span className="text-gray-300 text-xs">Dia selecionado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-brand-dark rounded"></div>
                  <span className="text-gray-300 text-xs">Hoje</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-success">{upcomingMeetings.length}</p>
                <p className="text-brand-gray text-sm mt-1">Agendamentos Ativos</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-brand-blue">-</p>
                <p className="text-brand-gray text-sm mt-1">Este Mês</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-warning">-</p>
                <p className="text-brand-gray text-sm mt-1">Taxa de Comparecimento</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-brand-primary">
                  {upcomingMeetings.length > 0
                    ? Math.round(upcomingMeetings.reduce((sum, m) => sum + m.duration, 0) / upcomingMeetings.length)
                    : '-'}
                  {upcomingMeetings.length > 0 && 'min'}
                </p>
                <p className="text-brand-gray text-sm mt-1">Duração Média</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (activeTab !== 'hub') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setActiveTab('hub')}
            className="text-brand-gray hover:text-white"
          >
            ← Voltar ao Hub
          </Button>
        </div>

        {activeTab === 'rapido' && <FeedbackRapido onSuccess={() => setActiveTab('hub')} />}
        {activeTab === 'completo' && <FeedbackCompleto onSuccess={() => setActiveTab('hub')} />}
        {activeTab === 'guia' && <GuiaPreenchimento />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 🚀 Background sync indicator (non-blocking) */}
      {isRefreshing && (
        <div className="flex items-center gap-2 text-xs text-brand-gray bg-brand-dark-light px-3 py-1.5 rounded-md">
          <RefreshCw className="w-3 h-3 animate-spin" />
          <span>Syncing...</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-bold">Formulários de Feedback</h2>
          <p className="text-brand-gray">Gerencie feedbacks rápidos e completos para sua equipe</p>
        </div>
        
        <div className="bg-success px-4 py-2 rounded-lg">
          <span className="text-black font-medium">Sistema de Feedback</span>
        </div>
      </div>

      {/* Estatísticas Rápidas */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card bg-brand-dark-light">
              <div className="p-4 text-center">
                <Skeleton className="h-8 w-16 mx-auto mb-2 bg-brand-dark-lighter" />
                <Skeleton className="h-4 w-24 mx-auto bg-brand-dark-lighter" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-error">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="btn-ghost mt-2 text-error hover:bg-error hover:text-white"
          >
            Tentar Novamente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-brand-blue">{feedbackStats?.sent || 0}</p>
              <p className="text-brand-gray text-sm">Feedbacks Registrados</p>
            </CardContent>
          </Card>
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-success">{feedbackStats?.byCategory.Reconhecimento || 0}</p>
              <p className="text-brand-gray text-sm">Reconhecimentos</p>
            </CardContent>
          </Card>
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-warning">{feedbackStats?.byCategory.Construtivo || 0}</p>
              <p className="text-brand-gray text-sm">Construtivos</p>
            </CardContent>
          </Card>
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-error">{feedbackStats?.byCategory.Crítico || 0}</p>
              <p className="text-brand-gray text-sm">Críticos</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State for No Feedbacks */}
      {!isLoading && !error && recentFeedbacks.length === 0 && (
        <Card className="bg-brand-dark border-brand-gray/10">
          <CardContent className="p-12">
            <div className="text-center max-w-2xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-brand-blue/10 rounded-monumenta flex items-center justify-center">
                  <MessageSquare className="h-16 w-16 text-brand-blue" />
                </div>
              </div>

              <h3 className="text-white text-2xl font-display mb-3">Comece sua jornada de feedback</h3>

              <p className="text-brand-gray text-base mb-8 leading-relaxed">
                Feedback contínuo fortalece relações e acelera o crescimento profissional. Use a metodologia COIN/SBI para dar feedbacks estruturados e efetivos. Escolha entre feedback rápido (5-10 min) para situações pontuais ou feedback completo (15-30 min) para projetos detalhados.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <button
                  onClick={() => setActiveTab('rapido')}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Registrar Primeiro Feedback
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
                <button
                  onClick={() => setActiveTab('guia')}
                  className="btn-secondary text-sm"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Ver Guia COIN/SBI
                </button>
                <button
                  onClick={() => setActiveTab('guia')}
                  className="btn-secondary text-sm"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Conhecer a Metodologia
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show main content only when there are feedbacks */}
      {!isLoading && !error && recentFeedbacks.length > 0 && (
        <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Tipos de Feedback */}
        <Card className="bg-brand-dark-light border-brand-gray/10 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">Tipos de Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              onClick={() => setActiveTab('rapido')}
              className="bg-brand-dark-lighter p-4 rounded-lg cursor-pointer hover:bg-brand-dark transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                    <Zap className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Feedback Rápido</h4>
                    <p className="text-brand-gray text-sm">Formato 1:1 para situações pontuais</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-white" />
              </div>
              <div className="mt-3 flex gap-2">
                <Badge variant="outline" className="text-warning border-warning">Rápido</Badge>
                <Badge variant="outline" className="text-brand-blue border-brand-blue">5-10 min</Badge>
              </div>
            </div>

            <div 
              onClick={() => setActiveTab('completo')}
              className="bg-brand-dark-lighter p-4 rounded-lg cursor-pointer hover:bg-brand-dark transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Feedback Completo</h4>
                    <p className="text-brand-gray text-sm">Formato detalhado para projetos e entregas</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-white" />
              </div>
              <div className="mt-3 flex gap-2">
                <Badge variant="outline" className="text-success border-success">Detalhado</Badge>
                <Badge variant="outline" className="text-brand-primary border-brand-primary">15-30 min</Badge>
              </div>
            </div>

            <div 
              onClick={() => setActiveTab('guia')}
              className="bg-brand-dark-lighter p-4 rounded-lg cursor-pointer hover:bg-brand-dark transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-blue/20 rounded-lg flex items-center justify-center">
                    <HelpCircle className="h-5 w-5 text-brand-blue" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Guia de Preenchimento</h4>
                    <p className="text-brand-gray text-sm">Metodologia COIN/SBI e exemplos</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-white" />
              </div>
              <div className="mt-3">
                <Badge variant="outline" className="text-brand-blue border-brand-blue">Guia</Badge>
              </div>
            </div>

            {/* HIDDEN: Card de Agenda dentro de Tipos de Feedback
            <div
              onClick={() => setActiveTab('agenda')}
              className="bg-brand-dark-lighter p-4 rounded-lg cursor-pointer hover:bg-brand-dark transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-primary/20 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-brand-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Agenda de 1-on-1</h4>
                    <p className="text-brand-gray text-sm">Gerencie agendamentos de feedbacks</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-white" />
              </div>
              <div className="mt-3 flex gap-2">
                <Badge variant="outline" className="text-brand-primary border-brand-primary">Agendamento</Badge>
                <Badge variant="outline" className="text-brand-gray border-brand-gray/20">Recorrente</Badge>
              </div>
            </div>
            */}
          </CardContent>
        </Card>

        {/* Feedbacks Recentes */}
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <CardTitle className="text-white">Feedbacks Registrados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-brand-dark-lighter p-3 rounded-lg">
                    <Skeleton className="h-6 w-full mb-2 bg-brand-dark" />
                    <Skeleton className="h-4 w-3/4 bg-brand-dark" />
                  </div>
                ))}
              </>
            ) : recentFeedbacks.length === 0 ? (
              <div className="text-center py-8 px-4">
                <div className="flex justify-center mb-3">
                  <div className="w-16 h-16 bg-brand-blue/10 rounded-monumenta flex items-center justify-center">
                    <MessageSquare className="h-12 w-12 text-brand-blue" />
                  </div>
                </div>
                <h4 className="text-white text-sm font-medium mb-1">Nenhum feedback ainda</h4>
                <p className="text-brand-gray text-xs mb-4">
                  Comece registrando seu primeiro feedback
                </p>
                <button
                  onClick={() => setActiveTab('rapido')}
                  className="btn-primary text-sm px-4 py-2"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Registrar Feedback
                </button>
              </div>
            ) : (
              recentFeedbacks.map((feedback) => (
                <div
                  key={feedback.feedbackId}
                  className="bg-brand-dark-lighter p-3 rounded-lg cursor-pointer hover:bg-brand-dark transition-colors"
                  onClick={() => handleFeedbackClick(feedback)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`${
                          feedback.category === 'Reconhecimento'
                            ? 'bg-success text-black'
                            : feedback.category === 'Construtivo'
                            ? 'bg-warning text-black'
                            : 'bg-error text-white'
                        }`}
                      >
                        {feedback.category}
                      </Badge>
                      <span className="text-white text-sm font-medium">{feedback.toEmployeeName}</span>
                    </div>
                    <span className="text-brand-gray text-xs">{formatRelativeDate(feedback.createdAt)}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{feedback.project || 'Sem projeto'}</p>
                  <p className="text-brand-gray text-xs mt-1">{translateStatus(feedback.status)}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ações Rápidas */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardHeader>
          <CardTitle className="text-white">Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Button
              onClick={() => setActiveTab('rapido')}
              className="bg-warning text-black hover:bg-warning h-auto p-4 flex-col gap-2"
            >
              <Zap className="h-6 w-6" />
              <span>Novo Feedback Rápido</span>
            </Button>

            <Button
              onClick={() => setActiveTab('completo')}
              className="bg-success text-black hover:bg-success h-auto p-4 flex-col gap-2"
            >
              <FileText className="h-6 w-6" />
              <span>Novo Feedback Completo</span>
            </Button>

            {/* HIDDEN: Schedule 1:1 button
            <Button
              onClick={() => setActiveTab('agenda')}
              className="bg-brand-primary text-white hover:bg-brand-primary h-auto p-4 flex-col gap-2"
            >
              <CalendarIcon className="h-6 w-6" />
              <span>Agendar 1-on-1</span>
            </Button>
            */}

            <Button
              variant="outline"
              onClick={() => navigate({ to: '/performance/feedbacks/received' })}
              className="text-white border-brand-gray/20 hover:bg-brand-dark-lighter h-auto p-4 flex-col gap-2"
            >
              <Users className="h-6 w-6" />
              <span>Histórico de Feedbacks</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Metodologia Destaque */}
      <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-brand-blue/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-blue/20 rounded-lg flex items-center justify-center">
              <HelpCircle className="h-6 w-6 text-brand-blue" />
            </div>
            <div className="flex-1">
              <h4 className="text-white font-medium">Metodologia COIN/SBI</h4>
              <p className="text-gray-300 text-sm">
                Estruture seus feedbacks de forma efetiva usando a metodologia 
                Contexto → Observação → Impacto → Next Step
              </p>
            </div>
            <Button 
              onClick={() => setActiveTab('guia')}
              variant="outline" 
              className="border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-black"
            >
              Ver Guia
            </Button>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      {/* Feedback Detail Modal */}
      <Dialog open={isFeedbackDetailOpen} onOpenChange={setIsFeedbackDetailOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedFeedback && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-white flex items-center gap-3">
                  {selectedFeedback.category === 'Crítico' ? (
                    <AlertTriangle className="h-6 w-6 text-error" />
                  ) : selectedFeedback.category === 'Construtivo' ? (
                    <Lightbulb className="h-6 w-6 text-warning" />
                  ) : (
                    <Star className="h-6 w-6 text-success" />
                  )}
                  Feedback {selectedFeedback.category}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-monumenta ${
                    selectedFeedback.category === 'Crítico' ? 'bg-error/10 border border-error/30' :
                    selectedFeedback.category === 'Construtivo' ? 'bg-warning/10 border border-warning/30' :
                    'bg-success/10 border border-success/30'
                  }`}>
                    <p className="text-brand-gray text-sm mb-1">Para:</p>
                    <p className="text-white font-medium">{selectedFeedback.toEmployeeName}</p>
                  </div>
                  <div className={`p-4 rounded-monumenta ${
                    selectedFeedback.category === 'Crítico' ? 'bg-error/10 border border-error/30' :
                    selectedFeedback.category === 'Construtivo' ? 'bg-warning/10 border border-warning/30' :
                    'bg-success/10 border border-success/30'
                  }`}>
                    <p className="text-brand-gray text-sm mb-1">Data:</p>
                    <p className="text-white font-medium">
                      {new Date(selectedFeedback.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Project */}
                {selectedFeedback.project && (
                  <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                    <p className="text-brand-gray text-sm mb-1">Projeto/Cliente:</p>
                    <p className="text-white">{selectedFeedback.project}</p>
                  </div>
                )}

                {/* COIN/SBI Sections */}
                <div className="space-y-4">
                  {/* Context */}
                  {selectedFeedback.context && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-brand-blue" />
                        <p className="text-brand-blue text-sm font-medium">CONTEXTO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.context}</p>
                    </div>
                  )}

                  {/* Observation */}
                  {selectedFeedback.observation && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-brand-primary" />
                        <p className="text-brand-primary text-sm font-medium">OBSERVAÇÃO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.observation}</p>
                    </div>
                  )}

                  {/* Impact */}
                  {selectedFeedback.impact && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-warning" />
                        <p className="text-warning text-sm font-medium">IMPACTO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.impact}</p>
                    </div>
                  )}

                  {/* Next Steps */}
                  {selectedFeedback.nextSteps && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowRight className="w-4 h-4 text-success" />
                        <p className="text-success text-sm font-medium">PRÓXIMOS PASSOS</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.nextSteps}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/20">
                <Button
                  variant="outline"
                  onClick={() => setIsFeedbackDetailOpen(false)}
                  className="border-brand-gray/20 text-gray-300 hover:bg-brand-dark-lighter"
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackHub;